import { useCallback, useEffect, useMemo, useState } from 'react';
import { fmtDateTime } from '../../../../utils/ruDates';

const METRIC_LABELS = {
  hits_view_search: 'Поисковые показы',
  search_position: 'Позиция в поиске',
  impressions: 'Показы',
  ctr: 'CTR',
  cart_rate: 'Конверсия в корзину',
  order_rate: 'Корзина → заказ',
  orders: 'Заказы',
};

const WHEN_MODES = [
  ['date', 'Дата'],
  ['time', 'Точное время'],
  ['period', 'Период'],
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function whenText(item) {
  if (item.happened_at) return fmtDateTime(item.happened_at);
  if (item.date_from && item.date_to && item.date_from !== item.date_to) return `${item.date_from} — ${item.date_to}`;
  return item.date_from || item.date_to || '—';
}

function EffectCell({ item }) {
  if (!item.measured) return <small className='oz-auto__muted'>контекст, не замеряем</small>;
  const effect = item.effect;
  if (!effect) return <small className='oz-auto__muted'>нет данных</small>;
  if (!effect.days_after) return <small className='oz-auto__muted'>после события ещё нет полных дней</small>;
  const rows = Object.entries(effect.metrics || {}).filter(([, value]) => value);
  if (!rows.length) return <small className='oz-auto__muted'>метрики не посчитать</small>;
  return <small>{rows.map(([key, value]) => (
    <span key={key} style={{ display: 'block', color: value.better == null ? undefined : value.better ? '#166534' : '#991b1b' }}>
      {METRIC_LABELS[key] || key}: {value.before} → {value.after}
      {value.change_pct != null && ` (${value.change_pct > 0 ? '+' : ''}${value.change_pct}%)`}
    </span>
  ))}<span className='oz-auto__muted'>неделя до: {effect.days_before} дн., после: {effect.days_after} дн.</span></small>;
}

export default function OzonAutomationMilestones({ base, request, targets, busy }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: 'INFOGRAPHIC', whenMode: 'date', date: today(), time: '', dateFrom: '', dateTo: '', comment: '', skus: [] });

  const products = useMemo(() => {
    const seen = new Map();
    (targets || []).forEach(t => { if (!seen.has(t.sku_id)) seen.set(t.sku_id, t.product_name || `SKU ${t.sku_id}`); });
    return [...seen.entries()];
  }, [targets]);

  const load = useCallback(async () => {
    setError('');
    try { setData(await request(`${base}/milestones`)); }
    catch (e) { setError(e.message || 'Не удалось загрузить события'); }
  }, [base, request]);

  useEffect(() => { load(); }, [load]);

  const toggleSku = sku => setForm(f => ({ ...f, skus: f.skus.includes(sku) ? f.skus.filter(s => s !== sku) : [...f.skus, sku] }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { kind: form.kind, sku_ids: form.skus, comment: form.comment };
      if (form.whenMode === 'time') body.happened_at = new Date(form.time).toISOString();
      if (form.whenMode === 'date') { body.date_from = form.date; body.date_to = form.date; }
      if (form.whenMode === 'period') { body.date_from = form.dateFrom; body.date_to = form.dateTo; }
      await request(`${base}/milestones`, { method: 'POST', body: JSON.stringify(body) });
      setForm(f => ({ ...f, comment: '', skus: [] }));
      await load();
    } catch (e) {
      setError(e.message || 'Не удалось сохранить событие');
    } finally {
      setSaving(false);
    }
  };

  const remove = async id => {
    if (!window.confirm('Удалить событие?')) return;
    try { await request(`${base}/milestones/${id}`, { method: 'DELETE' }); await load(); }
    catch (e) { setError(e.message || 'Не удалось удалить событие'); }
  };

  const whenFilled = form.whenMode === 'time' ? !!form.time
    : form.whenMode === 'date' ? !!form.date : !!(form.dateFrom && form.dateTo);
  const kinds = data?.kinds || [];

  return <section className='oz-auto__panel'>
    <h4>События группы</h4>
    <p className='oz-auto__muted'>
      Отмечайте, что вы сами поменяли в товарах — название, описание, характеристики, инфографику, видео,
      rich-контент, логистику — или что произошло вокруг («Иное»: например, ушёл конкурент). Биддер не оценивает
      сам контент: событие делит историю на «до» и «после», и для замеряемых типов видно, что стало с
      нужными метриками. «Иное» учитывается как контекст.
    </p>
    {error && <p role='alert' className='bidder__error'>{error}</p>}

    <div className='oz-auto__experiment' style={{ display: 'grid', gap: 10 }}>
      <label>Тип <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>
        {kinds.map(k => <option key={k.kind} value={k.kind}>{k.label}{k.measured ? '' : ' (контекст)'}</option>)}
      </select></label>

      <div>Когда
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '4px 0' }}>
          {WHEN_MODES.map(([value, label]) => <label key={value} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type='radio' checked={form.whenMode === value} onChange={() => setForm({ ...form, whenMode: value })} />{label}</label>)}
        </div>
        {form.whenMode === 'date' && <input type='date' value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />}
        {form.whenMode === 'time' && <input type='datetime-local' value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />}
        {form.whenMode === 'period' && <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          с <input type='date' value={form.dateFrom} onChange={e => setForm({ ...form, dateFrom: e.target.value })} />
          по <input type='date' value={form.dateTo} onChange={e => setForm({ ...form, dateTo: e.target.value })} />
          <small className='oz-auto__muted'>за момент события берётся середина периода</small></span>}
      </div>

      <div>Товары <small className='oz-auto__muted'>— ничего не выбрано = вся группа</small>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
          {products.map(([sku, name]) => <label key={sku} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type='checkbox' checked={form.skus.includes(sku)} onChange={() => toggleSku(sku)} />
            <span>{name} <small className='oz-auto__muted'>SKU {sku}</small></span></label>)}
        </div>
      </div>

      <label>Комментарий — что именно сделали
        <textarea rows={2} maxLength={1000} value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })}
          placeholder='Например: заменили главный слайд инфографики; добавили FBS-склад в Казани; конкурент ушёл из выдачи' />
      </label>

      <div><button type='button' disabled={busy || saving || !whenFilled || !data} onClick={save}>
        {saving ? 'Сохраняем…' : 'Добавить событие'}</button></div>
    </div>

    {!data ? <p>Загрузка…</p> : data.items.length === 0 ? <p className='oz-auto__muted'>Событий пока нет.</p> :
      <div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Тип</th><th>Когда</th><th>Товары</th><th>Комментарий</th><th>До → после</th><th /></tr></thead>
        <tbody>{data.items.map(item => <tr key={item.id}>
          <td>{item.kind_label}</td>
          <td>{whenText(item)}</td>
          <td>{item.sku_ids.length ? item.sku_ids.map(sku => (products.find(([s]) => s === sku) || [sku, `SKU ${sku}`])[1]).join(', ') : 'вся группа'}</td>
          <td>{item.comment || '—'}</td>
          <td><EffectCell item={item} /></td>
          <td><button type='button' disabled={busy} onClick={() => remove(item.id)}>Удалить</button></td>
        </tr>)}</tbody>
      </table></div>}
  </section>;
}
